import { IntlShape, useIntl } from "react-intl"
import ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { carrotClient } from "#/utils/CarrotClient"
import { useCallback } from "react"
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"

export interface TelemedicineForPediatricsEligibility {
  eligibilityRecordId: string | null
  registrationCompleted: boolean
  enrollmentUrl: string
  needsReactivation: boolean
}

export const GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY = ["getTelemedicineForPediatricsEligibility"]
const CREATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_MUTATION_KEY = ["createTelemedicineForPediatricsEligibility"]
const REFRESH_TELEMEDICINE_FOR_PEDIATRICS_ENROLLMENT_URL_MUTATION_KEY = [
  "refreshTelemedicineForPediatricsEnrollmentUrl"
]
const REACTIVATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_MUTATION_KEY = [
  "reactivateTelemedicineForPediatricsEligibility"
]

function useTelemedicineForPediatricsEligibility(): UseQueryResult<
  TelemedicineForPediatricsEligibility,
  ResponseError
> {
  return useQuery({
    queryKey: GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY,
    queryFn: () => carrotClient.getTelemedicineForPediatricsEligibility(),
    retry: buildCarrotApiRetryPolicy(1),
    refetchOnWindowFocus: false
  })
}

export function useCreateTelemedicineForPediatricsEligibility(): UseMutationResult<
  TelemedicineForPediatricsEligibility,
  ResponseError,
  string
> {
  return useMutation((email: string) => carrotClient.createTelemedicineForPediatricsEligibility(email), {
    mutationKey: CREATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_MUTATION_KEY,
    retry: false
  })
}

export function useRefreshTelemedicineForPediatricsEnrollmentUrl(): UseMutationResult<
  TelemedicineForPediatricsEligibility,
  ResponseError,
  void
> {
  return useMutation(() => carrotClient.refreshTelemedicineForPediatricsEnrollmentUrl(), {
    mutationKey: REFRESH_TELEMEDICINE_FOR_PEDIATRICS_ENROLLMENT_URL_MUTATION_KEY,
    retry: false
  })
}

export function useReactivateTelemedicineForPediatricsEligibility(): UseMutationResult<
  TelemedicineForPediatricsEligibility,
  ResponseError,
  void
> {
  return useMutation(() => carrotClient.reactivateTelemedicineForPediatricsEligibility(), {
    mutationKey: REACTIVATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_MUTATION_KEY,
    retry: false
  })
}

export function useInvalidateTelemedicineForPediatricsEligibility(): () => void {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY }),
    [queryClient]
  )
}

export type TelemedicineForPediatricsActivationStatus =
  | "not-started"
  | "in-progress"
  | "activated"
  | "needs-reactivation"

export function getTelemedicineForPediatricsActivationStatus(
  eligibility: TelemedicineForPediatricsEligibility | undefined
): TelemedicineForPediatricsActivationStatus {
  if (eligibility?.registrationCompleted && eligibility?.needsReactivation) {
    return "needs-reactivation"
  }
  if (eligibility?.registrationCompleted) {
    return "activated"
  }
  if (eligibility?.eligibilityRecordId) {
    return "in-progress"
  }
  return "not-started"
}

export function getCallToActionButtonText(
  intl: IntlShape,
  activationStatus: TelemedicineForPediatricsActivationStatus
): string {
  if (activationStatus === "needs-reactivation") {
    return intl.formatMessage({ defaultMessage: "Reactivate your Blueberry account" })
  }
  if (activationStatus === "activated") {
    return intl.formatMessage({ defaultMessage: "Access your Blueberry account" })
  }
  return intl.formatMessage({ defaultMessage: "Activate your Blueberry account" })
}

interface UseTelemedicineForPediatricsActivationStatusResult {
  activationStatus: TelemedicineForPediatricsActivationStatus
  ctaButtonText: string
  enrollmentUrl: string | undefined
  isLoading: boolean
  isError: boolean
  error: ResponseError
}

export function useTelemedicineForPediatricsActivationStatus(): UseTelemedicineForPediatricsActivationStatusResult {
  const intl = useIntl()
  const { data, isLoading, isError, error }: UseQueryResult<TelemedicineForPediatricsEligibility, ResponseError> =
    useTelemedicineForPediatricsEligibility()
  const activationStatus = getTelemedicineForPediatricsActivationStatus(data)
  const ctaButtonText = getCallToActionButtonText(intl, activationStatus)
  return {
    activationStatus,
    ctaButtonText,
    enrollmentUrl: data?.enrollmentUrl,
    isLoading,
    isError,
    error
  }
}
